import jwtDecode from "jwt-decode";
import * as Yup from 'yup';
import { getPropertyById, getUserDetails } from "../../../../SOA/api";
import { getBuildingInspectionById } from "./Api";

// Default item names matching backend controller defaults
export const defaultItemNames = {
  interior: [
    "Lobby", "Hallways", "Carpets", "Walls", "Interior Hallway Doors",
    "Interior Lighting", "Suite Doors from Outside", "Ceiling", "Staircase",
    "Garbage Room", "Laundry Room"
  ],
  elevators: [
    "No Sign of Damage", "Tracks Free of Debris", "Doors Operating Smoothly",
    "No Jerk or Weird Sound"
  ],
  underground: [
    "Sprinkler Room", "Elevator Room with Clear Access", "Maintenance Room",
    "Electrical Room", "Boiler Room", "Janitor Room", "Make Up Air Area",
    "UG Storage Cages", "UG Parking Door"
  ],
  outside: [
    "Landscaping/Snow Removal", "Sidewalks", "Fences", "Garbage Area",
    "Outdoor Parking", "Dog Run", "Roof Damages", "Side Entrance Doors",
    "Building Appearance", "Sides of Building", "Windows Closed (in winter)",
    "Patios of Residents"
  ],
  misc_others: [
    "Amenities (Fitness/Theatre/Lounge)", "Office"
  ]
};

const buildDefaultItem = (name, isInterior = false, isCustom = false) => ({
  name,
  needs_work: null,
  comments: '',
  attachments: [],
  due_date: null,
  completed: null,
  is_custom: isCustom,
  memos: [],
  last_edited_at: null
});

const buildDefaultSection = (names, isInterior = false) => {
  return names.map(name => buildDefaultItem(name, isInterior, false));
};

/**
 * Default initial values for a new Building Inspection
 */
export const defaultBuildingInspection = {
  inspection_date: new Date().toISOString().split('T')[0],
  property_details: {
    property_id: '',
    building_name: '',
  },
  interior: buildDefaultSection(defaultItemNames.interior, true),
  elevators: buildDefaultSection(defaultItemNames.elevators, false),
  underground: buildDefaultSection(defaultItemNames.underground, false),
  outside: buildDefaultSection(defaultItemNames.outside, false),
  misc_others: buildDefaultSection(defaultItemNames.misc_others, false),
  conclusions_recommendations: '',
  status: 'draft',
  completed_by: null,
  finalizedAt: null,
};

/**
 * Fetch property details and current user info
 */
export const getPropertyDetailsForInspection = async (propertyId) => {
  try {
    if (!propertyId) {
      throw new Error('Property ID is required');
    }

    const token = localStorage.getItem('accessToken');
    const user = token ? jwtDecode(token) : null;

    if (!user || !user.id || !user.role) {
      throw new Error('User details are missing from token');
    }

    const property = await getPropertyById(propertyId);
    if (!property) {
      throw new Error('Property not found');
    }

    let currentUserDetails = null;
    if (['technicalStaff', 'vendor', 'manager', 'inspector'].includes(user.role)) {
      try {
        currentUserDetails = await getUserDetails(user.id, user.role);
      } catch (err) {
        console.warn('Could not fetch user details for completed_by name');
      }
    }

    return {
      property,
      currentUser: {
        id: user.id,
        role: user.role,
        name:
          currentUserDetails?.first_name || currentUserDetails?.firstname
            ? `${currentUserDetails.first_name || currentUserDetails.firstname} ${currentUserDetails.last_name || currentUserDetails.lastname || ''}`.trim()
            : 'Unknown User',
      },
    };
  } catch (error) {
    console.error('Error fetching property details for inspection:', error);
    throw error;
  }
};

/**
 * Auto-fill property details when a property is selected
 */
export const autoFillPropertyDetails = async (
  currentFormValues,
  propertyId,
  buildingName = ''
) => {
  try {
    if (!propertyId) {
      return {
        ...currentFormValues,
        property_details: {
          property_id: '',
          building_name: '',
        },
      };
    }

    const { property } = await getPropertyDetailsForInspection(propertyId);

    let buildingNameToUse = buildingName || property.title || '';

    if (property.building && Array.isArray(property.building)) {
      const matched = property.building.find(b => b.buildingName === buildingName);
      if (matched) {
        buildingNameToUse = matched.buildingName;
      } else if (property.building.length > 0) {
        buildingNameToUse = property.building[0].buildingName || property.title;
      }
    }

    return {
      ...currentFormValues,
      property_details: {
        property_id: propertyId,
        building_name: buildingNameToUse,
      },
      inspection_date: currentFormValues.inspection_date || new Date().toISOString().split('T')[0],
    };
  } catch (error) {
    console.error('Failed to auto-fill property details:', error);
    return currentFormValues;
  }
};

/**
 * Get initial values for editing an existing Building Inspection
 */
export const getBuildingInspectionInitialValues = async (id) => {
  if (!id) return defaultBuildingInspection;

  try {
    const inspection = await getBuildingInspectionById(id);

    const normalizeItem = (item, isInterior = false) => ({
      name: item.name || 'Unknown Item',
      needs_work: item.needs_work ?? null,
      comments: item.comments || '',
      attachments: Array.isArray(item.attachments) ? item.attachments : [],
      due_date: item.due_date
        ? new Date(item.due_date).toISOString().split('T')[0]
        : null,
      completed: item.completed ?? null,
      is_custom: item.is_custom ?? false,
      memos: Array.isArray(item.memos)
        ? item.memos.map(m => ({
          content: m.content || '',
          createdBy: {
            role: m.createdBy?.role || 'unknown',
            object_id: m.createdBy?.object_id?.toString?.() || m.createdBy?.object_id || '',
          },
          createdAt: m.createdAt ? new Date(m.createdAt).toISOString() : new Date().toISOString(),
        }))
        : [],
      last_edited_at: item.last_edited_at || null,
    });

    const normalizeSection = (sectionData, sectionKey) => {
      const isInterior = sectionKey === 'interior';
      if (Array.isArray(sectionData) && sectionData.length > 0) {
        return sectionData.map(item => normalizeItem(item, isInterior));
      }
      return buildDefaultSection(defaultItemNames[sectionKey], isInterior);
    };

    return {
      _id: inspection._id?.toString(),
      inspection_date: inspection.inspection_date
        ? new Date(inspection.inspection_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      property_details: {
        property_id: inspection.property_details?.property_id?._id?.toString()
          || inspection.property_details?.property_id?.toString()
          || '',
        building_name: inspection.property_details?.building_name || '',
      },
      interior: normalizeSection(inspection.interior, 'interior'),
      elevators: normalizeSection(inspection.elevators, 'elevators'),
      underground: normalizeSection(inspection.underground, 'underground'),
      outside: normalizeSection(inspection.outside, 'outside'),
      misc_others: normalizeSection(inspection.misc_others, 'misc_others'),
      conclusions_recommendations: inspection.conclusions_recommendations || '',
      status: inspection.status || 'draft',
      completed_by: inspection.completed_by || null,
      finalizedAt: inspection.finalizedAt
        ? new Date(inspection.finalizedAt).toISOString()
        : null,
      history: Array.isArray(inspection.history) ? inspection.history : [],
    };
  } catch (error) {
    console.error('Failed to load inspection for edit:', error);
    return defaultBuildingInspection;
  }
};

/**
 * Validation Schema for Building Inspection Form
 */
export const BuildingInspectionValidationSchema = Yup.object().shape({
  inspection_date: Yup.date()
    .required('Inspection date is required')
    .typeError('Please select a valid date'),

  property_details: Yup.object().shape({
    property_id: Yup.string().required('Property selection is required'),
    building_name: Yup.string().required('Building name is required').trim(),
  }).required(),

  conclusions_recommendations: Yup.string()
    .trim()
    .required('Conclusions & Recommendations are required'),

  status: Yup.string()
    .oneOf(['draft', 'completed'])
    .required('Status is required'),
  interior: Yup.array().of(Yup.object()).optional(),
  elevators: Yup.array().of(Yup.object()).optional(),
  underground: Yup.array().of(Yup.object()).optional(),
  outside: Yup.array().of(Yup.object()).optional(),
  misc_others: Yup.array().of(Yup.object()).optional(),
});

export default defaultBuildingInspection;